// Spec 65 i18n-framework — deterministic case/accent folding for search boxes.
//
// WHY IT LIVES IN THE SDK RATHER THAN IN AN APP. It was born in customer-web
// and moved here the day operator-console's flag search turned out to carry
// the identical bare `.toLocaleLowerCase()` pair. Copying the file would have
// produced two definitions of "does izin match İzin", which is exactly the
// class of duplication that lets one console silently regress while the other
// stays fixed. Both SPAs already depend on @novemai/i18n-sdk, so no new
// dependency edge was created.
//
// WHY THIS EXISTS AT ALL. Every search box in this app used to call a bare
// `.toLocaleLowerCase()`. The no-argument form does not use the APP's language —
// it uses the HOST's locale, which for a browser is the OS setting and for a
// Vitest run is whatever LANG the shell exported. That made the same code
// produce different results on different machines, and both results were wrong
// for someone:
//
//   en host: "İzin".toLocaleLowerCase() === "i" + U+0307 + "zin"  → "izin" MISSES
//   tr host: "Invoice".toLocaleLowerCase() === "ınvoice"          → "invoice" MISSES
//
// The Turkish side of that is not an edge case in this product: `leave.*` is the
// largest notification family and every one of its subjects starts with "İzin".
// Passing "tr" explicitly would only swap which half of the users is broken, so
// the fix is to stop asking a locale at all.
//
// WHY ACCENT-STRIPPING RATHER THAN A NARROW i/İ/ı/I MAP. The narrow map was the
// first draft and was rejected: a reader on a US keyboard types "gorusme" for
// "görüşme" and "cikis" for "çıkış", and the narrow map leaves them with zero
// results on text they can see. NFD + dropping combining marks handles the whole
// Turkish set (ş ğ ü ö ç, plus the İ→I+U+0307 decomposition) in one rule instead
// of a per-letter table that the next language would have to extend. The cost is
// that folding is lossy — "resume" matches "résumé" — which is the expected and
// wanted behaviour in a search box, and is NOT acceptable anywhere else. This
// helper is for search comparison only; never store or display its output.
//
// U+0131 (ı) is the one character NFD cannot help with: it is not a decomposable
// form, so it survives the mark-stripping pass and needs the explicit mapping
// below. `I` and `İ` have already become `i` by that point, so all four members
// of the Turkish i-family land on the same folded character.

const COMBINING_MARKS = /[\u0300-\u036f]/g;
const DOTLESS_I = /\u0131/g;

/**
 * Fold `text` for search comparison: locale-independent, case-insensitive and
 * accent-insensitive.
 *
 * Apply to BOTH sides of the comparison — the haystack and the needle the user
 * typed. Folding one side only reintroduces exactly the mismatch this exists to
 * remove.
 */
export function foldForSearch(text: string): string {
  return text
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(DOTLESS_I, "i");
}
